/**
 * `html(markup, opts?)` on the blocks surface: restricted HTML and flex CSS compiled to the existing blocks, so the output is the same
 * engine data a hand-written `unit`/`stack`/`panel`/... call would give. The Layout behavior places and sizes the flow; html() writes
 * sizes and positions only where the page states them: px sizes through `fixed` (dots, swatches, % boxes), one px scale for type, gaps
 * and padding through `faithful` (a page that sets its sizes in px), and edges through `overlay` (absolutely placed layers).
 *
 * The markup is read by a small hand-written tokenizer (no dependency). Anything it does not know (a tag, an attribute, a CSS
 * property, a px width) is reported as a warning and skipped; malformed markup gets a best-effort tree plus a warning. It never throws
 * for input it cannot place. Wiring (`data-on`) and entrances (`data-enter`) are written as native Properties states and interactions,
 * never as scripts.
 *
 * Mapping, in short: div/section/... -> stack (a column unless `display:flex` row), `flex-wrap:wrap` -> row, class card/panel/muted/
 * primary/accent -> panel on that surface, `.split` -> split, h1/h2/h3/p/small -> display/title/heading/body/caption text, ul/li ->
 * bulleted texts, img -> image, figure -> figure, dl -> facts, span.chip -> chip, button -> button, `<stage3d name>` -> stage3d, inline `<svg>` -> flat
 * shapes (html-svg.ts).
 */
import { RADIUS, SPACING, SURFACES, type Surface } from './design-tokens';
import { cascade, parseAnimations, parseSheet, parseTransform, parseTransition, READ_CSS, type AnimationSpec, type Frame, type Pose, type Sheet, type TransitionSpec } from './css-motion';
import { applyCase, chooseFont, firstColor, nearestColor, parseBorder, parseColor, parseRadiusPx, parseShadows, parseSideBorders, rungForSize, toPx, type Rgba } from './html-style';
import { BUNDLED_FONTS, ITEM_MIN, THEME_DEFAULTS, type Rung } from './design-tokens';
import { compileSvg, inlineStyle, SVG_PAINT, SVG_READS, type SvgPlan } from './html-svg';
import { triangulate } from './mesh-earcut';

// ── Markup ──────────────────────────────────────────────

interface El { tag: string; attrs: Record<string, string>; kids: Node[] }
type Node = El | string;

const VOID = new Set(['img', 'br', 'hr', 'input', 'meta', 'link', 'stage3d']);
const RAW_TEXT = new Set(['script', 'style']);
const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', mdash: '—', ndash: '–', middot: '·', bull: '•' };
const decode = (s: string) => s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
    if (e[0] === '#') {
        const code = e[1].toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
        return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : m;
    }
    return ENTITIES[e.toLowerCase()] ?? m;
});

/** Read markup into a tree. Problems are reported through `warn`; the tree is always returned. */
export function parseMarkup(source: string, warn: (message: string) => void): El {
    const root: El = { tag: '#root', attrs: {}, kids: [] };
    const stack: El[] = [root];
    const top = () => stack[stack.length - 1];
    let i = 0;
    const n = source.length;
    while (i < n) {
        if (source[i] !== '<') {
            const next = source.indexOf('<', i + 1);
            const end = next < 0 ? n : next;
            // A lone "<" that does not open a tag is text.
            const text = source.slice(i, end);
            if (text.trim()) top().kids.push(decode(text));
            // The space between two inline elements is part of the line ("<a>One</a> <b>Two</b>").
            else if (top().kids.length && typeof top().kids[top().kids.length - 1] !== 'string' && next >= 0 && source[next + 1] !== '/') top().kids.push(' ');
            i = end;
            continue;
        }
        if (source.startsWith('<!--', i)) {
            const end = source.indexOf('-->', i + 4);
            if (end < 0) { warn('An HTML comment is not closed; the rest of the markup was skipped.'); break; }
            i = end + 3;
            continue;
        }
        if (source.startsWith('<!', i) || source.startsWith('<?', i)) {
            const end = source.indexOf('>', i);
            i = end < 0 ? n : end + 1;
            continue;
        }
        if (source[i + 1] === '/') {
            const end = source.indexOf('>', i);
            if (end < 0) { warn('A closing tag is not finished; the rest of the markup was skipped.'); break; }
            const tag = source.slice(i + 2, end).trim().toLowerCase();
            i = end + 1;
            if (VOID.has(tag)) continue;
            let at = stack.length - 1;
            while (at > 0 && stack[at].tag !== tag) at--;
            if (at <= 0) { warn(`Closing tag </${tag}> has no matching opening tag; it was ignored.`); continue; }
            for (let k = stack.length - 1; k > at; k--) warn(`<${stack[k].tag}> was not closed before </${tag}>; it was closed there.`);
            stack.length = at;
            continue;
        }
        const open = /^<([a-zA-Z][\w:-]*)/.exec(source.slice(i, i + 64));
        if (!open) {
            const text = '<';
            top().kids.push(text);
            i += 1;
            continue;
        }
        const tag = open[1].toLowerCase();
        i += open[0].length;
        const attrs: Record<string, string> = {};
        const attrRe = /\s*([^\s=/>"']+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/y;
        let closed = false;
        let selfClose = false;
        while (i < n) {
            if (source[i] === '>') { i++; closed = true; break; }
            if (source.startsWith('/>', i)) { i += 2; closed = true; selfClose = true; break; }
            attrRe.lastIndex = i;
            const m = attrRe.exec(source);
            if (!m || m[0].length === 0) { i++; continue; }
            attrs[m[1].toLowerCase()] = decode(m[2] ?? m[3] ?? m[4] ?? '');
            i = attrRe.lastIndex;
        }
        if (!closed) { warn(`Tag <${tag}> is not finished (no ">"); the rest of the markup was skipped.`); break; }
        const el: El = { tag, attrs, kids: [] };
        top().kids.push(el);
        if (RAW_TEXT.has(tag)) {
            const close = source.toLowerCase().indexOf(`</${tag}`, i);
            if (tag === 'style') el.kids.push(source.slice(i, close < 0 ? n : close));
            const end = close < 0 ? n : source.indexOf('>', close);
            i = end < 0 ? n : end + 1;
            continue;
        }
        if (!VOID.has(tag) && !selfClose) stack.push(el);
    }
    for (let k = stack.length - 1; k > 0; k--) warn(`<${stack[k].tag}> is not closed; it was closed at the end.`);
    return root;
}

// ── CSS ─────────────────────────────────────────────────

/** Declared CSS properties a block can use. Others are reported. */
const CSS_KNOWN = new Set(READ_CSS);
/** Motion declarations: read by `motion()`, not by the layout. */
const MOTION_CSS = /^(animation(-[a-z-]+)?|transition(-[a-z-]+)?|transform|opacity)$/;
/** A width and a height this small (px) are a dot or a swatch's own size; larger sizes are the layout's. */
const SMALL_PX = 48;
const CSS_SIZE = new Set(['width', 'height', 'min-width', 'max-width', 'min-height', 'max-height']);

/** A `set:` value as written: true, false, a finite number, or text. */
function setValue(raw: string): boolean | number | string {
    return raw === 'true' ? true : raw === 'false' ? false : raw !== '' && Number.isFinite(Number(raw)) ? Number(raw) : raw;
}

/** Prefix of a size declaration the layout does not take as written: the value stays in the style, and is reported if nothing reads it. */
const IGNORED_SIZE = '~';
function ignoredSizeMessage(where: string, name: string, value: string): string {
    return /px\b/.test(value) ? `${where}: ${name}:${value} is ignored; a px size is not used, the layout sizes blocks (use the unit preset and gap/padding tokens).`
        : `${where}: ${name}:${value} is ignored; the layout sizes blocks.`;
}

function parseStyle(source: string | undefined, where: string, warn: (message: string) => void): Record<string, string> {
    const out: Record<string, string> = {};
    for (const part of (source ?? '').split(';')) {
        const at = part.indexOf(':');
        if (at < 0) {
            if (part.trim()) warn(`${where}: "${part.trim()}" is not a CSS declaration; ignored.`);
            continue;
        }
        const name = part.slice(0, at).trim().toLowerCase();
        const value = part.slice(at + 1).trim().toLowerCase();
        if (!name) continue;
        if (name === 'width' && value === '100%') { out[name] = value; continue; }
        if ((name === 'width' || name === 'height') && /^\d*\.?\d+px$/.test(value) && parseFloat(value) <= SMALL_PX) { out[name] = value; continue; }
        if (CSS_SIZE.has(name)) {
            // Kept under IGNORED_SIZE so a box that can use it (a % size on a placed box, a stage filling its parent) reads it; the
            // ones nothing used are reported when the page is done.
            out[IGNORED_SIZE + name] = value;
            continue;
        }
        if (MOTION_CSS.test(name)) { out[name] = value; continue; }
        if (!CSS_KNOWN.has(name)) { warn(`${where}: CSS "${name}" is not supported; ignored.`); continue; }
        out[name] = value;
    }
    return out;
}

/** CSS length to px (`rem`/`em` at 16 px); null when it is not a plain length. */
function lengthPx(value: string | undefined): number | null {
    if (value === undefined) return null;
    const m = /^(-?\d*\.?\d+)\s*(px|rem|em)?$/.exec(value.trim());
    if (!m) return null;
    const v = parseFloat(m[1]);
    return m[2] === 'rem' || m[2] === 'em' ? v * 16 : v;
}

/** The unit width the tokens are shares of, as the prototype's reference canvas, in px. */
const CANVAS_PX = 1280;
function nearestToken<T extends string>(px: number, scale: Record<T, number>): T {
    let best: T | undefined;
    for (const token of Object.keys(scale) as T[]) {
        if (best === undefined || Math.abs(scale[token] * CANVAS_PX - px) < Math.abs(scale[best] * CANVAS_PX - px)) best = token;
    }
    return best!;
}

// ── Compiler ────────────────────────────────────────────

type Align = 'start' | 'center' | 'end' | 'stretch';
interface Opts { into?: string; name?: string }
interface ContainerOpts extends Opts { lead?: boolean; gap?: string; padding?: string; align?: Align }

/** What the compiler needs from the block tools and the collector; the block tools hand it their own functions. */
export interface HtmlKit {
    unit(kind: unknown, preset: unknown, opts?: Opts & { width?: number }): string;
    stack(dir: unknown, opts?: ContainerOpts & { inline?: boolean }): string;
    panel(surface?: unknown, opts?: ContainerOpts & { dir?: 'x' | 'y'; radius?: string }): string;
    row(opts?: ContainerOpts & { wrap?: boolean; minItem?: 's' | 'm' | 'l' }): string;
    split(opts?: Opts & { side?: 'start' | 'end'; gap?: string; padding?: string; align?: Align }): string;
    text(role: unknown, content: unknown, opts?: Opts & { align?: 'left' | 'center' | 'right'; bold?: boolean; look?: Look }): string;
    image(prompt: unknown, aspect?: unknown, opts?: Opts & { lead?: boolean }): string;
    figure(prompt: unknown, aspect: unknown, caption: unknown, opts?: Opts & { lead?: boolean }): string;
    button(label: unknown, opts?: Opts & { surface?: Surface; leading?: (id: string) => void; iconOnly?: boolean }): string;
    /** An inline svg drawn as flat shapes: `em` is its height in em of the text around it, `rung` that text's rung, `look.ink` what currentColor is. */
    svg(plan: SvgPlan, opts: Opts & { em: number; rung: Rung; look?: Look }): string;
    chip(label: unknown, opts?: Opts & { surface?: Surface; leading?: (id: string) => void; iconOnly?: boolean }): string;
    facts(items: unknown, opts?: ContainerOpts): string;
    stage3d(name?: unknown, aspect?: unknown, opts?: Opts & { lead?: boolean }): string;
    /** The unit a block sits in (or is). */
    unitOf(id: string): string | undefined;
    /** Units made so far. */
    units(): string[];
    find(id: string): { name: string; behaviors?: { type: string; overrides?: Record<string, unknown> }[] } | undefined;
    behavior(id: string, type: string, overrides: Record<string, unknown>): void;
    interaction(source: string): InteractionChain | undefined;
    /** Ask for a moving entrance on a block: it is given a slot and a content group once the layout is known. False when it cannot be. */
    enclose(id: string, kind: 'rise' | 'slide-left' | 'slide-right', delay: number): boolean;
    /** Hosted font families the deployment declares, beyond the bundled ones. */
    fonts?: readonly string[];
    /** A layer taken out of the flow: it fills `into` and places what it holds by `edges` (px against the 1280 reference; undefined is not set). */
    overlay(opts: { into: string; name?: string; edges?: { top?: number; right?: number; bottom?: number; left?: number }; behind?: boolean; depthStep?: number }): string;
    /** A block of a fixed size in px against the 1280 reference (a dot, a swatch, a box sized in %). `hugH`: with no height it keeps hugging its content. */
    fixed(id: string, size: { w?: number; h?: number; hugH?: boolean }): void;
    /** The page sets its sizes in px: type, gaps and padding keep one scale (px against the 1280 reference) instead of the ramp's rungs and tokens. */
    faithful(): void;
    /** A block that is as wide as its content in a column (a button), at `side` of it: the browser's inline-block, not a stretched item. */
    hug(id: string, side: 'Start' | 'Center' | 'End'): void;
    /** A block's share of its row, against its siblings' (a grid track's `fr`). */
    weigh(id: string, weight: number): void;
    /** The colour, type and case a block is drawn with; the block tools turn it into Style bindings (with the exact value as an override) and Text options. */
    look(id: string, look: Look): void;
    /** Ask for CSS motion on a block (a clip, an opacity animation, hover and press states). False when the block cannot carry it. */
    motion(id: string, request: MotionRequest): boolean;
    warn(message: string): void;
}

/** What the page's CSS asks of one block's look: exact colours (a Style role is picked for each) and type options. */
export interface Look {
    /** A panel's, button's or the unit body's fill. */
    fill?: Rgba;
    /** Text colour. */
    ink?: Rgba;
    /** A literal family; absent keeps the Style's font for the block's role. */
    fontFamily?: string;
    /** Set when the CSS gave a weight: bold from 600. */
    bold?: boolean;
    italic?: boolean;
    /** Em of the font size. */
    letterSpacing?: number;
    /** The font size the page sets, in px against the 1280 reference: one scale for every px size of the page. */
    fontPx?: number;
    /** A wrapping row's narrowest item in px (against the 1280 reference). */
    minItemPx?: number;
    /** A box's own gap and padding in px (same scale), in place of the nearest space token. */
    gapPx?: number;
    padPx?: number;
    /** A box's corner radius as a share of the 1280 px reference width (the unit's width scales it), and a pill when it is round. */
    radius?: number;
    pill?: boolean;
    /** Padding of a rim: the border's width, same share. */
    padding?: number;
    /** `box-shadow`s that fall outside the box, in px against the 1280 px reference: drawn as layers of relief behind it. */
    shadows?: { x: number; y: number; blur: number; color: Rgba }[];
    /** A shadow with no offset that is a glow: the box is lit from inside (emissive). */
    glow?: Rgba;
    /** `backdrop-filter` on the box: it is drawn translucent. */
    translucent?: boolean;
    /** Multiple of the font size. */
    lineHeight?: number;
}

/** What the CSS asks of one block, in px and degrees: the block tools resolve it against the unit's size. */
export interface MotionRequest {
    clip?: { anim: AnimationSpec; frames: Frame[] };
    fade?: { anim: AnimationSpec; frames: Frame[] };
    hover?: { pose?: Pose; opacity?: number };
    active?: { pose?: Pose; opacity?: number };
    transition?: TransitionSpec;
}
interface InteractionChain {
    onSelect(): {
        setProperty(target: string, property: string, value: unknown): unknown;
        setPropertyExpr(target: string, property: string, expression: string): unknown;
        setState(target: string, state?: string): unknown;
        setActiveChild(target: string, index: number): unknown;
        pagesNext(target: string, wrap?: boolean, animate?: boolean): unknown;
        pagesPrev(target: string, wrap?: boolean, animate?: boolean): unknown;
    };
    onStart(): { setGroupState(target: string, state?: string): unknown };
}

export interface HtmlOptions extends Opts {
    /** Families a page may name, beyond the bundled ones: the deployment's hosted fonts. */
    fonts?: readonly string[];
    /** Page kind and preset of the unit made when `into` is not given. */
    kind?: string;
    preset?: string;
    width?: number;
}

export interface HtmlResult {
    /** The unit the markup was built into. */
    unit: string;
    /** Handles by `<stage3d name>` and by element `id`, so spatial code in the same call can target them. */
    ids: Record<string, string>;
}

const CONTAINERS = new Set(['section', 'div', 'header', 'main', 'footer', 'nav', 'aside', 'article', 'form']);
const ROLE: Record<string, string> = { h1: 'display', h2: 'title', h3: 'heading', h4: 'heading', h5: 'heading', h6: 'heading', p: 'body', small: 'caption', label: 'caption', blockquote: 'body', dt: 'caption', dd: 'body', strong: 'body', b: 'body', em: 'body', i: 'body' };
const INLINE = new Set(['strong', 'b', 'em', 'i', 'span', 'a', 'code', 'br', 'u', 'mark', 'sub', 'sup', 'small', 'abbr', 'time']);
const KNOWN_ATTRS = new Set(['class', 'style', 'id', 'alt', 'name', 'title', 'lang', 'dir', 'role']);
const KNOWN_DATA = new Set(['data-aspect', 'data-on', 'data-target', 'data-enter', 'data-delay', 'data-lead', 'data-name']);
/** Seconds between the siblings of a group that all have `data-enter` when none of them has a `data-delay` (any `data-delay` hands the timing to the author). */
const ENTER_STAGGER = 0.08;
const ENTER_KINDS = ['rise', 'fade', 'slide-left', 'slide-right', 'scale'] as const;
type EnterKind = typeof ENTER_KINDS[number];
/** An entrance pose in a layout flow can only be a scale or a turn: positions come from the layout, and a state pose is absolute. */
const ENTER_FROM: Record<EnterKind, { scale: [number, number, number] }> = {
    fade: { scale: [0.92, 0.92, 0.92] }, rise: { scale: [1, 0.6, 1] }, 'slide-left': { scale: [0.6, 1, 1] }, 'slide-right': { scale: [0.6, 1, 1] }, scale: { scale: [0.5, 0.5, 0.5] },
};
const JUSTIFY: Record<string, string> = {
    'flex-start': 'Start', start: 'Start', center: 'Center', 'flex-end': 'End', end: 'End',
    'space-between': 'SpaceBetween', 'space-around': 'SpaceAround', 'space-evenly': 'SpaceEvenly',
};
const ALIGN_ITEMS: Record<string, Align> = { 'flex-start': 'start', start: 'start', center: 'center', 'flex-end': 'end', end: 'end', stretch: 'stretch', normal: 'stretch' };
const SURFACE_ORDER: Surface[] = ['primary', 'accent', 'muted', 'panel', 'card'];

export function compileHtml(markup: unknown, options: HtmlOptions | undefined, kit: HtmlKit): HtmlResult {
    const seen = new Set<string>();
    const warn = (message: string) => { if (!seen.has(message)) { seen.add(message); kit.warn(`html(): ${message}`); } };
    /** An svg's own style: what its shapes read (size, paint) is not reported; anything else is, as for any element. */
    const svgWarn = (message: string) => {
        const name = /^<svg>: (?:CSS ")?([a-z-]+)/.exec(message)?.[1];
        if (!name || !SVG_READS.has(name)) warn(message);
    };
    if (typeof markup !== 'string') {
        warn('markup must be a string; nothing was built.');
        markup = '';
    }
    const opts = options ?? {};
    let unit: string | undefined = opts.into !== undefined ? kit.unitOf(opts.into) : kit.units().length === 1 ? kit.units()[0] : undefined;
    const into = opts.into ?? unit;
    if (into === undefined) {
        unit = kit.unit(opts.kind ?? 'page', opts.preset ?? 'landscape', { name: opts.name ?? 'Page', ...(opts.width !== undefined ? { width: opts.width } : {}) });
    } else if (unit === undefined) {
        warn('"into" is not inside a unit; the markup was built there, but wiring and entrances need a unit.');
    }
    const root = into ?? unit!;
    const result: HtmlResult = { unit: unit ?? root, ids: {} };
    const entering: string[] = [];
    // A row of siblings that all enter, none with a delay of its own, cascades one step per sibling.
    const staggers = new Map<El, number>();

    const tree = parseMarkup(markup as string, warn);
    /** Names `set:` writes with text, or with values of more than one kind ("auto" and "3"): every set of them passes text. */
    const textProps = (() => {
        const kinds = new Map<string, Set<string>>();
        (function visit(el: El) {
            for (const action of (el.attrs['data-on'] ?? '').split('|')) {
                const m = /^\s*set\s*:\s*([A-Za-z_]\w*)\s*=(.*)$/i.exec(action);
                if (m) { const set = kinds.get(m[1]) ?? new Set<string>(); set.add(typeof setValue(m[2].trim())); kinds.set(m[1], set); }
            }
            for (const k of el.kids) if (typeof k !== 'string') visit(k);
        })(tree);
        return new Set([...kinds].filter(([, set]) => set.size > 1 || set.has('string')).map(([name]) => name));
    })();
    (function stagger(node: El) {
        const kids = node.kids.filter((k): k is El => typeof k !== 'string');
        if (kids.length > 1 && kids.every(k => (ENTER_KINDS as readonly string[]).includes(k.attrs['data-enter']) && k.attrs['data-delay'] === undefined)) {
            kids.forEach((k, i) => staggers.set(k, i * ENTER_STAGGER));
        }
        kids.forEach(stagger);
    })(tree);
    const sheet: Sheet = { keyframes: new Map(), rules: [] };
    const readStyles = (nodes: Node[]) => {
        for (const k of nodes) {
            if (typeof k === 'string') continue;
            if (k.tag === 'style') {
                // Svg paint (fill, stroke, ...) applies only to svg shapes, which read it, so it is not reported as unsupported.
                const one = parseSheet(k.kids.filter((x): x is string => typeof x === 'string').join(''), m => { const name = /^stylesheet: CSS "([a-z-]+)"/.exec(m)?.[1]; if (!name || !SVG_PAINT.has(name)) warn(m); });
                for (const [name, frames] of one.keyframes) sheet.keyframes.set(name, frames);
                for (const rule of one.rules) sheet.rules.push({ ...rule, order: sheet.rules.length });
            } else readStyles(k.kids);
        }
    };
    readStyles(tree.kids);

    // ── Style resolution: the cascade (stylesheet rules by specificity, then the inline style) plus the inherited properties.
    const hosted: readonly string[] = [...BUNDLED_FONTS, ...(opts.fonts ?? kit.fonts ?? [])];
    const parentOf = new Map<El, El>();
    /** Elements with an inline size the layout did not take as written, and which of those sizes something read after all. */
    const sizedEls = new Set<El>();
    const sizeRead = new Map<El, Set<string>>();
    const readSize = (el: El, prop: string) => { (sizeRead.get(el) ?? sizeRead.set(el, new Set()).get(el)!).add(prop); };
    const indexTree = (el: El) => { for (const k of el.kids) if (typeof k !== 'string') { parentOf.set(k, el); indexTree(k); } };
    indexTree(tree);
    const classesOf = (el: El) => (el.attrs.class ?? '').split(/\s+/).filter(Boolean);
    const ownMemo = new Map<El, Record<string, string>>();
    /** The declarations that apply to one element, known or not: stylesheet rules, then its inline style. */
    const styleOf = (el: El): Record<string, string> => {
        let v = ownMemo.get(el);
        if (!v) {
            // An svg's own size and paint are read by its shapes (html-svg.ts), so its inline style is not reported as unsupported.
            const inline = parseStyle(el.attrs.style, `<${el.tag}>`, el.tag === 'svg' ? svgWarn : warn);
            if (Object.keys(inline).some(k => k.startsWith(IGNORED_SIZE))) sizedEls.add(el);
            v = cascade(sheet, el.tag, classesOf(el), el.attrs.id, '', inline);
            ownMemo.set(el, v);
        }
        return v;
    };
    /** A size as the page declared it (the inline one first), whether or not the layout takes it as written. */
    const declared = (el: El, prop: string): string | undefined => { const d = styleOf(el); return d[IGNORED_SIZE + prop] ?? d[prop]; };
    interface Computed { size: number; sizeSet: boolean; color?: Rgba; family?: string; weight?: number; weightOwn: boolean; italic?: boolean; ls?: number; lh?: number; tt?: string; align?: string }
    const computedMemo = new Map<El, Computed>();
    const computedOf = (el: El | undefined): Computed => {
        if (!el) return { size: 16, sizeSet: false, weightOwn: false };
        const have = computedMemo.get(el);
        if (have) return have;
        const up = computedOf(parentOf.get(el));
        const d = styleOf(el);
        const where = `<${el.tag}>`;
        const c: Computed = { ...up, weightOwn: false };
        if (d['font-size'] !== undefined) {
            const px = toPx(d['font-size'], up.size);
            if (px !== null && px > 0) { c.size = px; c.sizeSet = true; } else warn(`${where}: font-size:${d['font-size']} is not a length; ignored.`);
        } else if (faithful) {
            // The browser's own sizes for headings and small print, against the parent's.
            if (UA_SIZE[el.tag] !== undefined) c.size = up.size * UA_SIZE[el.tag];
            c.sizeSet = true;
        }
        if (d.color !== undefined && d.color !== 'inherit') {
            const col = parseColor(d.color);
            if (col) c.color = col; else warn(`${where}: color:${d.color} is not a plain colour (hex, rgb, hsl or a name); ignored.`);
        }
        if (d['font-family'] !== undefined && d['font-family'] !== 'inherit') c.family = d['font-family'];
        if (d['font-weight'] !== undefined) {
            const w = d['font-weight'];
            c.weight = w === 'bold' || w === 'bolder' ? 700 : w === 'normal' || w === 'lighter' ? 400 : Number.isFinite(parseInt(w, 10)) ? parseInt(w, 10) : c.weight;
            c.weightOwn = true;
        }
        if (d['font-style'] !== undefined) c.italic = d['font-style'] === 'italic' || d['font-style'] === 'oblique';
        if (d['letter-spacing'] !== undefined) {
            const v = d['letter-spacing'];
            const em = /^(-?\d*\.?\d+)em$/.exec(v);
            const px = em ? null : toPx(v, c.size);
            c.ls = v === 'normal' ? undefined : em ? parseFloat(em[1]) : px !== null ? px / c.size : (warn(`${where}: letter-spacing:${v} is not a length; ignored.`), undefined);
        }
        if (d['line-height'] !== undefined) {
            const v = d['line-height'];
            c.lh = v === 'normal' ? undefined : /^\d*\.?\d+$/.test(v) ? parseFloat(v) : (toPx(v, c.size) ?? NaN) / (/%$/.test(v) ? 1 : c.size);
            if (c.lh !== undefined && (!Number.isFinite(c.lh) || c.lh <= 0)) { warn(`${where}: line-height:${v} is not understood; ignored.`); c.lh = undefined; }
            else if (c.lh !== undefined && /%$/.test(v)) c.lh = parseFloat(v) / 100;
        }
        if (d['text-transform'] !== undefined) c.tt = d['text-transform'];
        if (d['text-align'] !== undefined) c.align = d['text-align'];
        computedMemo.set(el, c);
        return c;
    };
    /** The body size the page's sizes are measured against: the size most of its characters are set in. */
    let baseMemo: number | undefined;
    const basePx = (): number => {
        if (baseMemo !== undefined) return baseMemo;
        const chars = new Map<number, number>();
        const walk = (el: El) => {
            const own = el.kids.filter((k): k is string => typeof k === 'string').join('').trim().length;
            if (own && !RAW_TEXT.has(el.tag)) { const c = computedOf(el); chars.set(c.size, (chars.get(c.size) ?? 0) + own); }
            for (const k of el.kids) if (typeof k !== 'string') walk(k);
        };
        walk(tree);
        baseMemo = [...chars.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0]?.[0] ?? 16;
        return baseMemo;
    };
    /** What a box's CSS draws outside its fill: border, corner radius, shadows, glow, backdrop blur. */
    const boxOf = (el: El) => {
        const d = styleOf(el);
        const ink = computedOf(el).color;
        const edge = parseBorder(d, ink) ?? undefined;
        const rpx = parseRadiusPx(d['border-radius'], 400);
        const pill = d['border-radius'] !== undefined && (/(^|\s)(50|[6-9]\d|100)%/.test(d['border-radius']) || (rpx !== null && rpx >= 60));
        const all = parseShadows(d['box-shadow']).filter(x => !x.inset && x.color.a > 0);
        const glowing = all.find(x => x.x === 0 && x.y === 0 && x.blur >= 6);
        return {
            edge, radius: rpx ?? undefined, pill,
            shadows: all.filter(x => x !== glowing).map(x => ({ x: x.x, y: x.y, blur: x.blur, color: x.color })),
            ...(glowing ? { glow: glowing.color } : {}),
            translucent: /blur\(/.test(d['backdrop-filter'] ?? ''),
        };
    };
    /** The colour behind an element: the nearest ancestor's fill, else the page's. */
    const behindOf = (el: El): Rgba | undefined => {
        for (let a = parentOf.get(el); a; a = parentOf.get(a)) { const f = fillOf(a); if (f) return f; }
        return undefined;
    };
    /** A page that sets a font size anywhere keeps its px sizes on one scale; one that sets none keeps the ramp's roles. */
    let faithful = false;
    const UA_SIZE: Record<string, number> = { h1: 2, h2: 1.5, h3: 1.17, h5: 0.83, h6: 0.67, small: 0.83, sub: 0.83, sup: 0.83 };
    const HEADINGISH = /^(h[1-6]|strong|b|th)$/;
    interface TextLook { look: Look; rung?: Rung; bold?: boolean }
    /** An element's text drawn as the page draws it: ink, face, weight, spacing, and the rung its size sits on. */
    const textLook = (el: El): TextLook => {
        const c = computedOf(el);
        const look: Look = {};
        if (c.color && c.color.a > 0) look.ink = c.color;
        if (c.family) {
            const choice = chooseFont(c.family, hosted);
            if (choice.family) look.fontFamily = choice.family;
            if (choice.note) warn(`<${el.tag}>: ${choice.note}`);
        }
        const bold = c.weight !== undefined && (c.weightOwn || !HEADINGISH.test(el.tag)) ? c.weight >= 600 : undefined;
        if (bold !== undefined) look.bold = bold;
        if (c.italic) look.italic = true;
        if (c.ls !== undefined && c.ls !== 0) look.letterSpacing = Math.round(c.ls * 1e4) / 1e4;
        if (c.lh !== undefined) look.lineHeight = Math.round(c.lh * 1e3) / 1e3;
        if (faithful && c.sizeSet) look.fontPx = Math.round(c.size * 100) / 100;
        return { look, ...(c.sizeSet ? { rung: rungForSize(c.size, basePx()) } : {}), ...(bold !== undefined ? { bold } : {}) };
    };
    /** A background colour on an element: the colour of `background-color`, or the first colour of `background`. */
    const fillOf = (el: El): Rgba | undefined => {
        const d = styleOf(el);
        const where = `<${el.tag}>`;
        const v = d['background-color'] ?? d.background;
        if (v === undefined) return undefined;
        if (/gradient\(/.test(v)) {
            const c = firstColor(v);
            if (c) warn(`${where}: a gradient background is drawn as its first colour.`);
            return c && c.a > 0 ? c : undefined;
        }
        if (/url\(/.test(v)) { warn(`${where}: a background image is not supported; ignored.`); const c = firstColor(v.replace(/url\([^)]*\)/g, '')); return c && c.a > 0 ? c : undefined; }
        const c = parseColor(v) ?? firstColor(v);
        if (!c) { if (v !== 'none' && v !== 'inherit') warn(`${where}: background:${v} is not a plain colour; ignored.`); return undefined; }
        return c.a > 0 ? c : undefined;
    };

    // ── Properties helpers: merge, never replace, since the blocks write theirs too.
    interface PropBag { properties?: { name: string; type: string; value: unknown }[]; states?: Record<string, unknown>; [key: string]: unknown }
    const propsOf = (id: string): PropBag => ({ ...(kit.find(id)?.behaviors?.find(b => b.type === 'Properties')?.overrides ?? {}) });
    const setProps = (id: string, patch: PropBag) => kit.behavior(id, 'Properties', { ...propsOf(id), ...patch });
    const declare = (owner: string, name: string, value: unknown) => {
        const type = typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'string';
        const list = (propsOf(owner).properties ?? []).slice();
        const have = list.find(p => p.name === name);
        if (have) {
            if (have.type !== type) warn(`property "${name}" is declared as ${have.type}; a ${type} value was used for it.`);
            return;
        }
        list.push({ name, type, value: type === 'number' ? 0 : type === 'boolean' ? false : '' });
        setProps(owner, { properties: list });
    };

    // ── Text
    const textOf = (nodes: Node[]): string => nodes.map(k => typeof k === 'string' ? k : k.tag === 'br' ? ' ' : k.tag === 'svg' ? '' : textOf(k.kids)).join('').replace(/\s+/g, ' ').trim();
    /** An inline element sits in its parent's line of text; one the CSS makes a block, flex or grid box does not. */
    const runnable = (k: El): boolean => INLINE.has(k.tag) && !hasClass(k, 'chip') && !/^(block|flex|grid|list-item|table)$/.test(styleOf(k).display ?? '') && !isDot(k);
    const allInline = (nodes: Node[]): boolean => nodes.every(k => typeof k === 'string' || (runnable(k) && allInline(k.kids)));
    const hasClass = (el: El, c: string) => (el.attrs.class ?? '').split(/\s+/).includes(c);
    const surfaceOf = (el: El): Surface | undefined => SURFACE_ORDER.find(s => hasClass(el, s));
    const wholeStrong = (el: El): boolean => {
        const real = el.kids.filter(k => typeof k !== 'string' || k.trim());
        return real.length === 1 && typeof real[0] !== 'string' && (real[0].tag === 'strong' || real[0].tag === 'b');
    };

    function checkAttrs(el: El, where: string) {
        for (const name of Object.keys(el.attrs)) {
            if (KNOWN_ATTRS.has(name) || KNOWN_DATA.has(name) || name.startsWith('aria-') || (el.tag === 'svg' && SVG_READS.has(name))) continue;
            if (name === 'src') warn(`${where}: src is ignored; an image is generated from its alt text.`);
            else if (name === 'href') warn(`${where}: href is ignored; links are not supported (use a button with data-on).`);
            else if (name.startsWith('data-')) warn(`${where}: attribute "${name}" is not known; ignored.`);
            else if (name.startsWith('on')) warn(`${where}: "${name}" is ignored; scripts do not run (use data-on).`);
            else warn(`${where}: attribute "${name}" is not known; ignored.`);
        }
        for (const c of (el.attrs.class ?? '').split(/\s+/).filter(Boolean)) {
            if (sheet.rules.some(r => r.classes.includes(c))) continue;
            if (c === 'background' || !((SURFACES as readonly string[]).includes(c) || c === 'split' || c === 'chip')) warn(`${where}: class "${c}" is not known; ignored.`);
        }
    }

    const placed = new Set<El>();
    const px = (v: string | undefined): number | null => (v === undefined || /%$/.test(v) ? null : lengthPx(v.trim()));
    const size = (el: El): number => 1 + el.kids.reduce((n: number, k) => n + (typeof k === 'string' ? 0 : size(k)), 0);
    /** A placed box becomes a layer when it is a backdrop (all four edges at 0) or a small mark; a placed column of content stays in the flow. */
    const layerOk = (el: El): boolean => {
        const d = styleOf(el);
        const edges = ['top', 'right', 'bottom', 'left'].map(k => d[k]);
        const bleed = (d.inset !== undefined && /^0(px)?(\s+0(px)?)*$/.test(d.inset.trim())) || edges.every(e => e !== undefined && /^0(px)?$/.test(e));
        const tall = px(d.top) !== null && px(d.bottom) !== null && (px(d.top) as number) <= 1 && (px(d.bottom) as number) <= 1;
        if (bleed || tall || el.tag === 'stage3d' || Number(d['z-index']) < 0) return true;
        return size(el) <= 8 && textOf(el.kids).length <= 80;
    };
    /** A layer needs a parent that holds in-flow content to take its size from; a box whose children are all placed takes them in flow. */
    const hasFlowSibling = (el: El): boolean => {
        const up = parentOf.get(el);
        if (!up || up === tree || up.tag === 'body' || up.tag === 'html') return true;
        if (placedRows(up.kids).rows.some(r => r.length > 0 && !r.includes(el))) return true;
        return up.kids.some(k => k !== el && (typeof k === 'string' ? k.trim().length > 0 : !/^(absolute|fixed)$/.test(styleOf(k).position ?? '') && !RAW_TEXT.has(k.tag)));
    };
    // ── Sizes the page's CSS fixes in px against the 1280 reference. The layout sizes most blocks itself, so a % size is read only where
    // the box it is a share of is known here: a placed box against its containing block, a stage against its parent's px height.
    const pxLength = (v: string | undefined): number | undefined => (v === undefined || /%$/.test(v) ? undefined : lengthPx(v) ?? undefined);
    const percentOf = (v: string | undefined, base: number | undefined): number | undefined => {
        const m = v === undefined ? null : /^(-?\d*\.?\d+)%$/.exec(v.trim());
        return m && base !== undefined ? base * parseFloat(m[1]) / 100 : undefined;
    };
    /** The padding the layout gives a box: one length on all sides, the largest the page wrote (as tokens() reads it). null when it is not in px. */
    const padPxOf = (el: El): number | null => {
        const v = styleOf(el).padding;
        if (v === undefined) return 0;
        const lengths = v.split(/\s+/).map(lengthPx);
        return lengths.every(l => l !== null) ? Math.max(...lengths.map(l => l ?? 0)) : null;
    };
    /** A flex row or a grid with several items in flow shares its width out, so what each gets is the layout's to work out. */
    const sharesWidth = (el: El): boolean => {
        const d = styleOf(el);
        const row = /^(inline-)?flex$/.test(d.display ?? '') && !(d['flex-direction'] ?? d['flex-flow'] ?? '').startsWith('column');
        const items = el.kids.filter(k => typeof k !== 'string' && !/^(absolute|fixed)$/.test(styleOf(k).position ?? ''));
        return (row || /^(inline-)?grid$/.test(d.display ?? '')) && items.length > 1;
    };
    /** A placed box that a % size gave px: the px, and the declarations that gave them (which are then not reported as ignored). */
    const placedSize = new Map<El, { w?: number; h?: number; reads: [El, string][] }>();
    /** A box's width in px: the unit's, less the padding of what it sits in, unless one of those sets a px width, which is the base from there. null-ish when it is not known. */
    const boxWidth = (box: El): number | undefined => {
        if (!(opts.into === undefined || opts.into === unit)) return undefined;
        const chain: El[] = [];
        for (let a: El | undefined = box; a; a = parentOf.get(a)) chain.unshift(a);
        let w = CANVAS_PX;
        for (const a of chain) {
            w = placedSize.get(a)?.w ?? pxLength(declared(a, 'width')) ?? w;
            if (a === box) break;
            const pad = padPxOf(a);
            if (pad === null || sharesWidth(a)) return undefined;
            w -= 2 * pad;
        }
        return w > 0 ? w : undefined;
    };
    /** A box's px height: its `height` or `min-height` (the larger, as the browser would have it). */
    const boxHeight = (box: El): number | undefined => {
        const known = ['height', 'min-height'].map(p => pxLength(declared(box, p))).filter((x): x is number => x !== undefined);
        return known.length ? Math.max(...known) : undefined;
    };
    /** What a child's `height:100%` is a share of: the box's height, less its padding (the page's boxes are border-box). */
    const innerHeight = (box: El): number | undefined => {
        const h = placedSize.get(box)?.h ?? boxHeight(box);
        const pad = padPxOf(box);
        return h === undefined || pad === null || h - 2 * pad <= 0 ? undefined : h - 2 * pad;
    };
    /** The box a placed element's % edges and sizes are shares of: its parent when that is positioned or is the page; else not known. */
    const containingBlock = (el: El): { w?: number; h?: number } => {
        const up = parentOf.get(el);
        if (!up || styleOf(el).position !== 'absolute') return {};
        if (up !== tree && up.tag !== 'html' && up.tag !== 'body' && !/^(relative|absolute|fixed|sticky)$/.test(styleOf(up).position ?? '')) return {};
        return { w: boxWidth(up), h: boxHeight(up) };
    };
    /** A placed box sized in %: its block takes the px it came to. `hugH`: with no height it keeps hugging its content. */
    const applyPlacedSize = (el: El, id: string, hugH: boolean) => {
        const size = placedSize.get(el);
        if (!size) return;
        kit.fixed(id, { ...(size.w !== undefined ? { w: size.w } : {}), ...(size.h !== undefined ? { h: size.h } : {}), ...(hugH ? { hugH: true } : {}) });
        for (const [owner, prop] of size.reads) readSize(owner, prop);
    };

    const nameFor = (el: El, fallback: string) => (el.attrs['data-name'] || el.attrs.name || el.attrs.id || fallback).slice(0, 40);

    // ── Containers
    function directionOf(css: Record<string, string>, where: string): 'x' | 'y' {
        const display = css.display;
        if (display !== undefined && display !== 'flex' && display !== 'block' && display !== 'inline-flex' && display !== 'flex-direction') {
            warn(`${where}: display:${display} is not supported; laid out as a column.`);
        }
        const flow = css['flex-flow']?.split(/\s+/) ?? [];
        const direction = css['flex-direction'] ?? flow.find(f => f.startsWith('row') || f.startsWith('column'));
        const isFlex = display === 'flex' || display === 'inline-flex';
        if (direction !== undefined && !isFlex) warn(`${where}: flex-direction needs display:flex; ignored.`);
        if (!isFlex) return 'y';
        return direction === undefined || direction.startsWith('row') ? 'x' : 'y';
    }
    function tokens(css: Record<string, string>, where: string): { gap?: string; padding?: string; radius?: string; gapPx?: number; padPx?: number } {
        const out: { gap?: string; padding?: string; radius?: string; gapPx?: number; padPx?: number } = {};
        const one = (prop: string, scale: Record<string, number>) => {
            const value = css[prop];
            if (value === undefined) return undefined;
            const lengths = value.split(/\s+/).map(lengthPx);
            const px = lengths.every(l => l !== null) ? Math.max(...lengths.map(l => l ?? 0)) : null;
            if (px === null) { warn(`${where}: ${prop}:${value} is not a px or rem length; ignored.`); return undefined; }
            if (faithful && (prop === 'gap' || prop === 'column-gap' || prop === 'row-gap')) out.gapPx = px;
            if (faithful && prop === 'padding') out.padPx = px;
            return nearestToken(px, scale);
        };
        const gapProp = css.gap !== undefined ? 'gap' : css['column-gap'] !== undefined ? 'column-gap' : css['row-gap'] !== undefined ? 'row-gap' : undefined;
        if (gapProp) out.gap = one(gapProp, SPACING);
        out.padding = one('padding', SPACING);
        out.radius = one('border-radius', RADIUS);
        return out;
    }
    /** A grid's columns: `repeat(n, ...)`, a list of tracks, or an auto-fit row of minimum width. */
    function gridOf(css: Record<string, string>, where: string): { columns: number; minItem?: 's' | 'm' | 'l'; minPx?: number; weights?: number[] } {
        const v = css['grid-template-columns'];
        if (v === undefined || v === 'none') return { columns: 1 };
        const auto = /repeat\(\s*auto-(?:fit|fill)\s*,\s*minmax\(\s*([\d.]+)(px|rem)/.exec(v);
        if (auto) {
            return { columns: 2, minItem: nearestToken(parseFloat(auto[1]) * (auto[2] === 'rem' ? 16 : 1), ITEM_MIN), minPx: parseFloat(auto[1]) * (auto[2] === 'rem' ? 16 : 1) };
        }
        const rep = /^repeat\(\s*(\d+)\s*,/.exec(v.trim());
        if (rep) return { columns: Math.max(1, parseInt(rep[1], 10)) };
        let depth = 0, tracks = 0, inTrack = false;
        for (const ch of v) {
            if (ch === '(') depth++;
            if (ch === ')') depth--;
            if (/\s/.test(ch) && depth === 0) inTrack = false;
            else if (!inTrack) { tracks++; inTrack = true; }
        }
        if (!tracks) warn(`${where}: grid-template-columns:${v} is not understood; laid out as a column.`);
        // Tracks in fr keep their ratio; fixed tracks (px) share by their size.
        const sizes = v.replace(/minmax\([^)]*\)/g, '1fr').split(/\s+/).map(t => /fr$/.test(t) ? parseFloat(t) : /px$/.test(t) ? parseFloat(t) / 100 : 1);
        const base = Math.min(...sizes.filter(x => x > 0));
        const weights = sizes.length === tracks && sizes.some(x => x !== sizes[0]) ? sizes.map(x => Math.round(x / base * 1e3) / 1e3) : undefined;
        return { columns: Math.max(1, tracks), ...(weights ? { weights } : {}) };
    }
    /** The row gap and the column gap of a grid, as space tokens. */
    function gapTokens(css: Record<string, string>, where: string): { row?: string; col?: string } {
        const parts = css.gap?.split(/\s+/);
        const pick = (v: string | undefined) => { const px = lengthPx(v); return px === null ? undefined : nearestToken(px, SPACING); };
        void where;
        return { row: pick(css['row-gap'] ?? parts?.[0]), col: pick(css['column-gap'] ?? parts?.[1] ?? parts?.[0]) };
    }
    const SURFACE_FILLS = Object.fromEntries((SURFACES as readonly string[]).map(x => [x, THEME_DEFAULTS[`${x}Color`]]));
    const nearestSurface = (hex: string): Surface => nearestColor(hex, SURFACE_FILLS) as Surface;

    /** A button is inline-block: it is as wide as its label unless the page asks for the full width, or its parent is a flex column that stretches. Returns where it sits when it hugs. */
    const hugsInColumn = (el: El): 'Start' | 'Center' | 'End' | undefined => {
        const css = styleOf(el);
        if (css.width === '100%' || (css['align-self'] ?? '') === 'stretch') return undefined;
        const up = parentOf.get(el);
        const pcss = up ? styleOf(up) : {};
        const flex = /^(inline-)?flex$/.test(pcss.display ?? '');
        if (flex) {
            const dir = pcss['flex-direction'] ?? pcss['flex-flow']?.split(/\s+/).find(f => f.startsWith('row') || f.startsWith('column')) ?? 'row';
            if (dir.startsWith('row')) return undefined;
            const ai = pcss['align-items'];
            if (ai === undefined || ai === 'normal' || ai === 'stretch') return undefined;
            return ai === 'center' ? 'Center' : ai === 'flex-end' || ai === 'end' ? 'End' : 'Start';
        }
        const a = up ? computedOf(up).align : undefined;
        return a === 'center' ? 'Center' : a === 'right' || a === 'end' ? 'End' : 'Start';
    };
    /** Dividers and underlines: a one-sided border is a thin bar of that colour and thickness along that side, in a layer over the box. */
    function sideBars(el: El, id: string, css: Record<string, string>) {
        for (const b of parseSideBorders(css, computedOf(el).color)) {
            const horizontal = b.side === 'top' || b.side === 'bottom';
            const edges = horizontal ? { [b.side]: 0, left: 0, right: 0 } : { [b.side]: 0, top: 0, bottom: 0 };
            const layer = kit.overlay({ into: id, name: `${nameFor(el, el.tag)} ${b.side} line Layer`, edges });
            const bar = kit.panel(nearestSurface(b.color.hex), { into: layer, name: `${nameFor(el, el.tag)} ${b.side} line`, gap: 's0', padding: 's0' });
            kit.look(bar, { fill: b.color });
            kit.fixed(bar, horizontal ? { h: Math.max(1, b.width) } : { w: Math.max(1, b.width) });
        }
    }
    /** A small empty box the page sizes itself and colours or outlines: a dot or a swatch (a button with only an aria-label too). */
    const isDot = (el: El): boolean => dotSize(styleOf(el)) !== undefined && textOf(el.kids) === '' && !el.kids.some(k => typeof k !== 'string' && k.tag === 'img')
        && (fillOf(el) !== undefined || boxOf(el).edge !== undefined);
    /** A small box with no content that the page sizes itself: a dot or a swatch. */
    const dotSize = (css: Record<string, string>): { w: number; h: number } | undefined => {
        const w = css.width && /^\d*\.?\d+px$/.test(css.width) ? parseFloat(css.width) : NaN;
        const h = css.height && /^\d*\.?\d+px$/.test(css.height) ? parseFloat(css.height) : NaN;
        return w > 0 && h > 0 && w <= SMALL_PX && h <= SMALL_PX ? { w, h } : undefined;
    };
    /** One zone leads a composition: the first one asked for does, and a second is reported. */
    let leadTaken = false;
    const wantsLead = (el: El, css: Record<string, string>) => {
        const flex = css['flex-grow'] ?? css.flex?.split(/\s+/)[0];
        const grow = flex === undefined ? NaN : parseFloat(flex);
        return (el.attrs['data-lead'] !== undefined && el.attrs['data-lead'] !== 'false') || grow >= 2;
    };
    const claimLead = (el: El, css: Record<string, string>): { lead?: true } => {
        if (!wantsLead(el, css)) return {};
        if (leadTaken) { warn(`<${el.tag}> "${nameFor(el, el.tag)}" is also marked as the lead; a composition has one, so the first one leads.`); return {}; }
        leadTaken = true;
        return { lead: true };
    };

    function container(el: El, parent: string): string {
        const where = `<${el.tag}>`;
        const css = styleOf(el);
        const grid = /^(inline-)?grid$/.test(css.display ?? '') ? gridOf(css, where) : undefined;
        const dir = grid ? 'y' : directionOf(css, where);
        const t = tokens(css, where);
        const align = css['align-items'] === undefined ? undefined : ALIGN_ITEMS[css['align-items']];
        if (css['align-items'] !== undefined && !align) warn(`${where}: align-items:${css['align-items']} is not supported; ignored.`);
        const o: ContainerOpts = {
            into: parent, name: nameFor(el, el.tag[0].toUpperCase() + el.tag.slice(1)),
            ...(dir === 'x' && /^(inline-)?flex$/.test(css.display ?? '') && !grid ? { content: true } : {}),
            ...(t.gap ? { gap: t.gap } : {}), ...(t.padding ? { padding: t.padding } : {}), ...(align ? { align } : {}),
        };
        const surface = surfaceOf(el);
        const lead = claimLead(el, css);
        const wrap = dir === 'x' && css['flex-wrap'] === 'wrap' || css['flex-flow']?.includes('wrap') === true && dir === 'x';
        let id: string;
        if (hasClass(el, 'split')) {
            const real = el.kids.filter(k => typeof k !== 'string' || k.trim());
            if (real.length === 2 && real.every(k => typeof k !== 'string')) {
                id = kit.split({ into: parent, name: o.name, ...(t.gap ? { gap: t.gap } : {}), ...(t.padding ? { padding: t.padding } : {}), ...(align ? { align } : {}) });
                // The half marked lead (data-lead or flex:2) is the main zone; with none marked, the first is.
                real.forEach(k => compileNode(k, id));
                return id;
            }
            warn(`${where}: a .split holds ${real.length} children; it needs exactly two. Built as a row.`);
        }
        const fill = fillOf(el);
        const box = boxOf(el);
        const dot = dotSize(css);
        if (dot && box.radius !== undefined && box.radius * 2 >= Math.min(dot.w, dot.h)) box.pill = true;
        const asPanel: Surface | undefined = surface ?? (fill ? nearestSurface(fill.hex) : box.edge || box.shadows.length ? 'background' : undefined);
        // A box the page only colours or outlines (no panel class) takes no padding or radius the CSS did not ask for.
        const bare = surface === undefined && asPanel !== undefined;
        const radiusToken = t.radius ?? (bare ? 'r1' : undefined);
        let rim: string | undefined;
        if (box.edge && asPanel) {
            // A border is a rim: an outer panel in the border's colour, padded by its width, holding the box.
            rim = kit.panel(nearestSurface(box.edge.color.hex), { into: parent, name: `${o.name} Rim`, gap: 's0', padding: 's0', dir: 'y', ...lead, ...(radiusToken ? { radius: radiusToken } : {}) });
            kit.look(rim, { fill: box.edge.color, padding: box.edge.width / CANVAS_PX, ...(box.radius !== undefined ? { radius: box.radius / CANVAS_PX, ...(box.pill ? { pill: true } : {}) } : {}) });
            o.into = rim;
        }
        const leadOpts = rim ? {} : lead;
        const inner: ContainerOpts = bare && !t.padding ? { ...o, padding: 's0' } : o;
        const rowGap = grid ? gapTokens(css, where) : undefined;
        const boxLook = (id: string) => {
            const radius = box.radius === undefined ? undefined : Math.max(0, box.radius - (box.edge?.width ?? 0));
            const behind = box.edge && !fill ? behindOf(el) : undefined;
            const look: Look = {
                ...(t.gapPx !== undefined ? { gapPx: t.gapPx } : {}), ...(t.padPx !== undefined ? { padPx: t.padPx } : {}),
                ...(fill ? { fill } : behind ? { fill: behind } : {}),
                ...(radius !== undefined ? { radius: radius / CANVAS_PX, ...(box.pill ? { pill: true } : {}) } : {}),
                ...(box.shadows.length ? { shadows: box.shadows } : {}), ...(box.glow ? { glow: box.glow } : {}), ...(box.translucent ? { translucent: true } : {}),
            };
            if (Object.keys(look).length) kit.look(id, look);
        };
        if (grid && (grid.columns > 1 || grid.minItem)) {
            if (grid.minItem && !asPanel) { id = kit.row({ ...inner, ...leadOpts, wrap: true, minItem: grid.minItem, ...(rowGap?.row ? { gap: rowGap.row } : {}) }); if (faithful && grid.minPx) kit.look(id, { minItemPx: grid.minPx }); el.kids.filter(k => typeof k !== 'string' || k.trim()).forEach(k => compileNode(k, id)); }
            else {
                id = asPanel ? kit.panel(asPanel, { ...inner, ...leadOpts, dir: 'y', ...(radiusToken ? { radius: radiusToken } : {}), ...(rowGap?.row ? { gap: rowGap.row } : {}) })
                    : kit.stack('y', { ...inner, ...leadOpts, ...(rowGap?.row ? { gap: rowGap.row } : {}) });
                const items = el.kids.filter(k => typeof k !== 'string' || k.trim());
                if (grid.minItem) {
                    const r = kit.row({ into: id, name: `${o.name} cells`, wrap: true, minItem: grid.minItem, ...(rowGap?.col ? { gap: rowGap.col } : {}) });
                    if (faithful && grid.minPx) kit.look(r, { minItemPx: grid.minPx });
                    items.forEach(k => compileNode(k, r));
                } else {
                    const weights = grid.weights;
                    for (let at = 0; at < items.length; at += grid.columns) {
                        const r = kit.row({ into: id, name: `${o.name} row ${at / grid.columns + 1}`, wrap: false, ...(rowGap?.col ? { gap: rowGap.col } : {}) });
                        items.slice(at, at + grid.columns).forEach((k, n) => {
                            const cell = compileNode(k, r);
                            if (cell && weights && weights[n] !== undefined && weights[n] !== 1) kit.weigh(cell, weights[n]);
                        });
                    }
                }
            }
            boxLook(id);
            sideBars(el, id, css);
            return rim ?? id;
        }
        if (grid) warn(`${where}: display:grid with one column (or none declared) is laid out as a column.`);
        if (asPanel) id = kit.panel(asPanel, { ...inner, ...leadOpts, dir, ...(radiusToken ? { radius: radiusToken } : {}) });
        else if (dot) id = kit.stack(dir, { ...o, ...leadOpts });
        else if (wrap) id = kit.row({ ...o, ...leadOpts, wrap: true });
        else id = kit.stack(dir, { ...o, ...leadOpts });
        boxLook(id);
        sideBars(el, id, css);
        if (dot) kit.fixed(id, { w: Math.max(1, dot.w - 2 * (box.edge?.width ?? 0)), h: Math.max(1, dot.h - 2 * (box.edge?.width ?? 0)) });
        else applyPlacedSize(el, id, true);
        if (!asPanel && t.radius) warn(`${where}: border-radius only applies to a box with a background, border or shadow, or a panel class; ignored.`);
        const justify = css['justify-content'];
        if (justify !== undefined) {
            if (JUSTIFY[justify]) kit.behavior(id, 'Layout', { alignMain: JUSTIFY[justify] });
            else warn(`${where}: justify-content:${justify} is not supported; ignored.`);
        }
        compileChildren(el.kids, id, el, dir === 'x' || /^(inline-)?flex$/.test(css.display ?? ''));
        return rim ?? id;
    }

    // ── Wiring
    function wire(el: El, button: string) {
        const spec = el.attrs['data-on'];
        if (spec === undefined) return;
        const targetName = el.attrs['data-target'];
        let target = unit;
        if (targetName !== undefined && targetName !== '') {
            target = kit.find(targetName) ? targetName : undefined;
            if (!target) { warn(`<button> "${nameFor(el, 'button')}": data-target "${targetName}" names no object in this build; not wired.`); return; }
        }
        if (!target) { warn(`<button> "${nameFor(el, 'button')}": no unit to wire to; not wired.`); return; }
        const source = kit.interaction(button);
        if (!source) { warn('interactions are not available in this build; buttons were not wired.'); return; }
        const select = source.onSelect();
        let wired = 0;
        for (const raw of spec.split('|')) {
            const action = raw.trim();
            if (!action) continue;
            const [kind, ...rest] = action.split(':');
            const arg = rest.join(':').trim();
            const k = kind.trim().toLowerCase();
            if (k === 'set') {
                const eq = arg.indexOf('=');
                const name = eq < 0 ? '' : arg.slice(0, eq).trim();
                const raw = eq < 0 ? '' : arg.slice(eq + 1).trim();
                if (!/^[A-Za-z_]\w*$/.test(name)) { warn(`data-on "${action}": set needs "set:name=value"; ignored.`); continue; }
                const value: unknown = textProps.has(name) ? raw : setValue(raw);
                declare(target, name, value);
                select.setProperty(target, name, value);
            } else if (k === 'toggle') {
                if (!/^[A-Za-z_]\w*$/.test(arg)) { warn(`data-on "${action}": toggle needs "toggle:name"; ignored.`); continue; }
                declare(target, arg, false);
                select.setPropertyExpr(target, arg, `!${arg}`);
            } else if (k === 'state') {
                if (!arg) { warn(`data-on "${action}": state needs "state:Name"; ignored.`); continue; }
                select.setState(target, arg);
            } else if (k === 'next') select.pagesNext(target);
            else if (k === 'prev') select.pagesPrev(target);
            else if (k === 'goto') {
                const index = Number(arg);
                if (!Number.isInteger(index) || index < 0) { warn(`data-on "${action}": goto needs a whole number from 0; ignored.`); continue; }
                select.setActiveChild(target, index);
            } else { warn(`data-on "${action}" is not known (set, toggle, state, next, prev, goto); ignored.`); continue; }
            wired++;
        }
        if (!wired) warn(`<button> "${nameFor(el, 'button')}": data-on "${spec}" wired nothing.`);
    }

    // ── Motion
    function enter(el: El, id: string) {
        // A delay only ever times an entrance, so a data-delay written without data-enter rises in rather than being lost.
        const kind = el.attrs['data-enter'] ?? (el.attrs['data-delay'] !== undefined ? 'rise' : undefined);
        if (kind === undefined) return;
        if (!(ENTER_KINDS as readonly string[]).includes(kind)) { warn(`data-enter="${kind}" is not known (${ENTER_KINDS.join(', ')}); ignored.`); return; }
        const rawDelay = el.attrs['data-delay'];
        let delay = 0;
        if (rawDelay !== undefined) {
            const m = /^(\d*\.?\d+)\s*(ms|s)?$/.exec(rawDelay.trim());
            if (!m) warn(`data-delay="${rawDelay}" is not a number of milliseconds (or seconds with an s suffix); used 0.`);
            else {
                delay = m[2] === 's' ? parseFloat(m[1]) : parseFloat(m[1]) / 1000;
                if (!m[2] && delay > 0 && delay < 0.01) warn(`data-delay="${rawDelay}" is under 10 ms (a bare number is milliseconds); write "${rawDelay.trim()}s" for seconds.`);
            }
        } else if (staggers.has(el)) delay = staggers.get(el)!;
        if (kind === 'rise' || kind === 'slide-left' || kind === 'slide-right') {
            // Moves inside the block's slot; where that is not possible it grows in instead.
            if (kit.enclose(id, kind, Math.round(delay * 1e3) / 1e3)) { entering.push(id); return; }
            warn(`data-enter="${kind}" needs a block inside a unit; it grows in instead.`);
        }
        const have = propsOf(id);
        const fades = kind === 'fade' && kit.find(id)?.behaviors?.some(b => b.type === 'Text');
        if (fades) {
            // Text carries an opacity, so it fades without changing size.
            const list = (have.properties ?? []).filter(p => p.name !== 'enterFade');
            list.push({ name: 'enterFade', type: 'number', value: 1 });
            kit.behavior(id, 'Text', { opacity: '{enterFade}' });
            setProps(id, {
                properties: list,
                initialState: 'away',
                states: {
                    ...(have.states ?? {}),
                    away: { values: { enterFade: 0 }, active: false, transition: { duration: 0 } },
                    home: { values: { enterFade: 1 }, active: true, transition: { duration: 0.6, delay: Math.round(delay * 1e3) / 1e3, easing: 'easeOut' } },
                },
            });
            entering.push(id);
            return;
        }
        setProps(id, {
            properties: have.properties ?? [],
            initialState: 'away',
            states: {
                ...(have.states ?? {}),
                away: { values: {}, active: false, transition: { duration: 0 } },
                home: { values: {}, active: true, enterFrom: ENTER_FROM[kind as EnterKind], transition: { duration: 0.6, delay: Math.round(delay * 1e3) / 1e3, easing: 'easeOut' } },
            },
        });
        entering.push(id);
    }

    // ── CSS motion
    function motion(el: El, id: string) {
        const where = `<${el.tag}>`;
        const classes = (el.attrs.class ?? '').split(/\s+/).filter(Boolean);
        const inline = parseStyle(el.attrs.style, where, el.tag === 'svg' ? svgWarn : warn);
        const base = cascade(sheet, el.tag, classes, el.attrs.id, '', inline);
        const hover = cascade(sheet, el.tag, classes, el.attrs.id, ':hover', {});
        const active = cascade(sheet, el.tag, classes, el.attrs.id, ':active', {});
        const animated = base.animation !== undefined || base['animation-name'] !== undefined;
        if (!animated && base.transition === undefined && base['transition-property'] === undefined && !Object.keys(hover).length && !Object.keys(active).length) {
            if (base.transform !== undefined || base.opacity !== undefined) warn(`${where}: a static transform or opacity is ignored; animate it with @keyframes or give it a :hover rule.`);
            return;
        }
        if (base.transform !== undefined || base.opacity !== undefined) warn(`${where}: a static transform or opacity is ignored; the block keeps its own pose.`);
        const request: MotionRequest = {};
        for (const anim of animated ? parseAnimations(base, where, warn) : []) {
            const keyframes = sheet.keyframes.get(anim.name);
            if (!keyframes) { warn(`${where}: animation "${anim.name}" has no @keyframes; ignored.`); continue; }
            const moves = keyframes.frames.some(f => f.pose);
            const fades = keyframes.frames.some(f => f.opacity !== undefined);
            if (!moves && !fades) { warn(`${where}: @keyframes ${anim.name} sets neither transform nor opacity; ignored.`); continue; }
            if (moves && request.clip || fades && request.fade) { warn(`${where}: a second animation of ${moves && request.clip ? 'transform' : 'opacity'} (${anim.name}) is ignored; one animation drives each.`); continue; }
            if (moves && anim.delay < 0) warn(`${where}: a negative animation-delay on a transform animation starts it from the first frame.`);
            if (moves) request.clip = { anim, frames: keyframes.frames };
            if (fades) request.fade = { anim, frames: keyframes.frames };
        }
        const transition = parseTransition(base, where, warn) ?? parseTransition(hover, where, warn) ?? parseTransition(active, where, warn);
        if (transition) request.transition = transition;
        for (const [key, decls] of [['hover', hover], ['active', active]] as const) {
            if (!Object.keys(decls).length) continue;
            const ask: { pose?: Pose; opacity?: number } = {};
            if (decls.transform !== undefined) ask.pose = parseTransform(decls.transform, `${where}:${key}`, warn);
            if (decls.opacity !== undefined) {
                const v = parseFloat(decls.opacity);
                if (Number.isFinite(v)) ask.opacity = Math.min(1, Math.max(0, v)); else warn(`${where}:${key}: opacity ${decls.opacity} is not a number; ignored.`);
            }
            for (const name of Object.keys(decls)) {
                if (!['transform', 'opacity'].includes(name) && !/^(transition|animation)/.test(name)) warn(`${where}:${key}: CSS "${name}" is not supported (transform and opacity are); ignored.`);
            }
            if (ask.pose || ask.opacity !== undefined) request[key] = ask;
            if (transition && !transition.properties.includes('transform') && ask.pose) warn(`${where}:${key}: transform changes with the transition only listing opacity; it moves with the same timing.`);
        }
        if (!request.clip && !request.fade && !request.hover && !request.active) return;
        if (!kit.motion(id, request)) { warn(`${where}: motion needs a block inside a unit; ignored.`); return; }
        if (request.fade && Number.isFinite(request.fade.anim.iterations)) entering.push(id);
    }

    // ── Nodes
    /** The runs of an element's inline content that differ in how they are set: text and inline children grouped while ink, face, weight, style and size agree. */
    function styleRuns(el: El): { el: El; text: string }[] {
        const key = (e: El) => { const t = textLook(e); const c = computedOf(e); return JSON.stringify([t.look.ink?.hex, t.look.fontFamily, t.bold, t.look.italic, c.sizeSet ? c.size : 0, c.tt]); };
        const out: { el: El; text: string; key: string }[] = [];
        const add = (e: El, nodes: Node[]) => {
            const text = textOf(nodes);
            if (!text) return;
            const k = key(e);
            const last = out[out.length - 1];
            if (last && last.key === k) last.text += ' ' + text; else out.push({ el: e, text, key: k });
        };
        let loose: Node[] = [];
        const flushLoose = () => { if (loose.length) { add(el, loose); loose = []; } };
        for (const k of el.kids) {
            if (typeof k === 'string' || k.tag === 'br') loose.push(k);
            else { flushLoose(); add(k, [k]); }
        }
        flushLoose();
        return out;
    }

    /** One text block for an element, set as the page sets it: its rung from its size, its ink, face, weight, spacing and case. */
    function emitText(el: El, role: string, content: string, opts: { into: string; name?: string; bold?: boolean; align?: 'left' | 'center' | 'right' }): string {
        const c = computedOf(el);
        const tl = textLook(el);
        const align = c.align === 'center' || c.align === 'right' || c.align === 'left' ? c.align : undefined;
        const bold = tl.bold ?? opts.bold;
        return kit.text(tl.rung ?? role, applyCase(content, c.tt), {
            into: opts.into, ...(opts.name ? { name: opts.name } : {}), ...(opts.align ?? align ? { align: opts.align ?? align } : {}),
            ...(bold !== undefined ? { bold } : {}), look: tl.look,
        });
    }

    /**
     * A container's children. In normal flow, adjacent text and inline elements are one line of text; in a flex or grid container each
     * child is its own item, so an inline child there is its own Text. `ctx` is the element whose style the text inherits.
     */
    /** The box a placed element takes in px against the 1280 reference: all four edges set, and not a backdrop. */
    const placedBox = (k: Node): { el: El; l: number; r: number; w: number; t: number } | undefined => {
        if (typeof k === 'string') return undefined;
        const d = styleOf(k);
        if (d.position !== 'absolute' && d.position !== 'fixed') return undefined;
        const px = (v: string | undefined) => { const n = v === undefined || /%$/.test(v) ? null : lengthPx(v.trim()); return n; };
        const [t, r, b, l] = [px(d.top), px(d.right), px(d.bottom), px(d.left)];
        if (t === null || r === null || b === null || l === null || k.tag === 'stage3d' || RAW_TEXT.has(k.tag)) return undefined;
        if ([t, r, b, l].every(e => e <= 1) || (t <= 1 && b <= 1) || Number(d['z-index']) < 0) return undefined;
        const w = CANVAS_PX - l - r;
        return w > 8 ? { el: k, l, r, w, t } : undefined;
    };
    /**
     * Boxes a page places side by side with `position:absolute` (columns, a bottom bar of labels): those that start at about the same
     * height and do not overlap across are one row, in order from the left, each taking the share of the width its box has. Anything else
     * placed stays a layer (or in the flow).
     */
    function placedRows(nodes: Node[]): { rows: El[][]; single: boolean } {
        const mark = (e: El) => textOf(e.kids).length <= 2 && size(e) <= 2;
        const boxes = nodes.map(placedBox).filter((x): x is NonNullable<typeof x> => x !== undefined && !mark(x.el)).sort((a, b) => a.t - b.t || a.l - b.l);
        const bands: typeof boxes[] = [];
        for (const box of boxes) {
            const band = bands.find(x => Math.abs(x[0].t - box.t) <= 48);
            if (band) band.push(box); else bands.push([box]);
        }
        const sorted = bands.map(band => band.sort((a, b) => a.l - b.l))
            .map(band => band.every((x, i) => i === 0 || x.l >= band[i - 1].l + band[i - 1].w - 8) ? band : band.slice(0, 1));
        // Several placed boxes are a layout of their own, read top to bottom; one or two are marks and stay layers.
        const flowAll = boxes.length >= 3 || sorted.some(b => b.length > 1);
        return { rows: sorted.filter(b => flowAll || b.length > 1).map(b => b.map(x => x.el)), single: flowAll };
    }

    function compileChildren(nodes: Node[], parent: string, ctx?: El, flex = false) {
        const { rows } = placedRows(nodes);
        const rowOf = new Map<El, El[]>();
        for (const row of rows) for (const el of row) rowOf.set(el, row);
        const made = new Set<El[]>();
        let run: Node[] = [];
        const flush = () => {
            if (!run.length) return;
            const content = textOf(run);
            const only = run.filter(k => typeof k !== 'string' || k.trim());
            const bold = only.length === 1 && typeof only[0] !== 'string' && (only[0].tag === 'strong' || only[0].tag === 'b');
            run = [];
            if (content) emitText(ctx ?? tree, 'body', content, { into: parent, ...(bold ? { bold: true } : {}) });
        };
        for (const k of nodes) {
            if (typeof k !== 'string' && rowOf.has(k)) {
                const row = rowOf.get(k)!;
                if (made.has(row)) continue;
                made.add(row);
                flush();
                const one = row.length === 1;
                const holder = kit.stack('x', { into: parent, name: `${nameFor(row[0], row[0].tag)} row`, gap: 's2', align: 'start', ...(one ? { content: true } : {}) });
                if (one) {
                    const only = placedBox(row[0])!;
                    kit.behavior(holder, 'Layout', { alignMain: only.l <= 40 ? 'Start' : only.r <= 40 ? 'End' : 'Center' });
                }
                for (const el of row) {
                    placed.add(el);
                    const cell = compileNode(el, holder);
                    const box = placedBox(el)!;
                    if (cell && !one) kit.weigh(cell, Math.round(Math.max(box.w, 160) / 100 * 1e3) / 1e3);
                }
                continue;
            }
            if (typeof k === 'string') run.push(k);
            else if (!flex && runnable(k) && k.tag !== 'small' && allInline(k.kids)) run.push(k);
            else { flush(); compileNode(k, parent); }
        }
        flush();
    }

    /** What an inline `<svg>` draws (null, with a note, when nothing in it can be), worked out once. */
    const svgPlans = new Map<El, { plan: SvgPlan; ink?: Rgba } | null>();
    function planSvg(el: El): { plan: SvgPlan; ink?: Rgba } | null {
        if (svgPlans.has(el)) return svgPlans.get(el)!;
        const sheetOf = (n: { tag: string; attrs: Record<string, string> }) => cascade(sheet, n.tag, (n.attrs.class ?? '').split(/\s+/).filter(Boolean), n.attrs.id, '', {});
        const own = { ...sheetOf(el), ...inlineStyle(el.attrs.style) };
        const up = computedOf(parentOf.get(el));
        const hidden = (own.display ?? el.attrs.display) === 'none' || /^(hidden|collapse)$/.test(own.visibility ?? el.attrs.visibility ?? '');
        const plan = hidden ? null : compileSvg({ node: el, sheetOf, fontPx: up.size, warn }, triangulate);
        const ink = (own.color !== undefined ? parseColor(own.color) : null) ?? up.color;
        const made = plan ? { plan, ...(ink ? { ink } : {}) } : null;
        svgPlans.set(el, made);
        return made;
    }
    /** An inline `<svg>` as flat shapes sized like an item of the text around it. */
    function emitSvg(el: El, parent: string): string | undefined {
        const made = planSvg(el);
        if (!made) return;
        const up = computedOf(parentOf.get(el));
        return kit.svg(made.plan, {
            into: parent, name: nameFor(el, 'Icon'), em: made.plan.height / up.size, rung: rungForSize(up.size, basePx()),
            ...(made.ink ? { look: { ink: made.ink } } : {}),
        });
    }
    const svgKids = (el: El): El[] => el.kids.filter((k): k is El => typeof k !== 'string' && k.tag === 'svg');

    function compileNode(node: Node, parent: string): string | undefined {
        if (typeof node === 'string') {
            const content = node.replace(/\s+/g, ' ').trim();
            if (content) kit.text('body', content, { into: parent });
            return;
        }
        const el = node;
        const where = `<${el.tag}>`;
        if (el.tag !== 'html' && el.tag !== 'body') checkAttrs(el, where);
        let id: string | undefined;
        const lead = () => claimLead(el, styleOf(el));
        const cssPos = styleOf(el);
        if (cssPos.display === 'none' || (cssPos.opacity === '0' && cssPos.animation === undefined && cssPos['animation-name'] === undefined)) {
            warn(`${where}: ${cssPos.display === 'none' ? 'display:none' : 'opacity:0'}, so it is hidden; not built.`);
            return;
        }
        if ((cssPos.position === 'absolute' || cssPos.position === 'fixed') && !placed.has(el) && hasFlowSibling(el) && el.tag !== 'html' && el.tag !== 'body' && !RAW_TEXT.has(el.tag) && el.tag !== 'br' && layerOk(el)) {
            placed.add(el);
            // A % edge or size is a share of the containing block; where that is not known it is dropped, as before.
            const cb = containingBlock(el);
            const edge = (v: string | undefined, base: number | undefined): number | undefined => {
                if (v === undefined) return undefined;
                if (/%$/.test(v)) return percentOf(v, base);
                const px = lengthPx(v.trim());
                return px === null ? undefined : px;
            };
            let edges = { top: edge(cssPos.top, cb.h), right: edge(cssPos.right, cb.w), bottom: edge(cssPos.bottom, cb.h), left: edge(cssPos.left, cb.w) };
            if (cssPos.inset !== undefined) {
                const [t, r = t, b = t, l = r] = cssPos.inset.split(/\s+/);
                edges = { top: edges.top ?? edge(t, cb.h), right: edges.right ?? edge(r, cb.w), bottom: edges.bottom ?? edge(b, cb.h), left: edges.left ?? edge(l, cb.w) };
            }
            const wide = percentOf(declared(el, 'width'), cb.w);
            const high = percentOf(declared(el, 'height'), cb.h);
            if ((wide !== undefined && wide > 0) || (high !== undefined && high > 0)) {
                placedSize.set(el, {
                    ...(wide !== undefined && wide > 0 ? { w: wide } : {}), ...(high !== undefined && high > 0 ? { h: high } : {}),
                    reads: [...(wide !== undefined && wide > 0 ? [[el, 'width'] as [El, string]] : []), ...(high !== undefined && high > 0 ? [[el, 'height'] as [El, string]] : [])],
                });
            }
            const z = parseInt(cssPos['z-index'] ?? '0', 10);
            const bleed = Object.values(edges).every(e => e !== undefined && e <= 1);
            const tall = edges.top !== undefined && edges.bottom !== undefined && edges.top <= 1 && edges.bottom <= 1;
            const behind = (Number.isFinite(z) && z < 0) || ((bleed || tall) && (el.tag === 'stage3d' || !(z > 0)));
            const clean = Object.fromEntries(Object.entries(edges).filter(([, v]) => v !== undefined)) as { top?: number; right?: number; bottom?: number; left?: number };
            const ov = kit.overlay({ into: parent, name: `${nameFor(el, el.tag)} Layer`, edges: clean, ...(behind ? { behind: true } : {}), depthStep: Number.isFinite(z) && z > 0 ? Math.min(z, 6) : 0 });
            const only = el.kids.filter((k): k is El => typeof k !== 'string');
            // A layer that only holds the 3D stage is the stage: it fills the layer, as the canvas filled its box.
            if (only.length === 1 && only[0].tag === 'stage3d' && textOf(el.kids) === '') {
                placed.add(only[0]);
                const boxSize = placedSize.get(el);
                if (boxSize) placedSize.set(only[0], boxSize);
                return compileNode(only[0], ov);
            }
            return compileNode(el, ov);
        }
        if (el.tag === 'html' || el.tag === 'body') {
            // The page itself: its colour is the unit's, its type is what every block inside inherits.
            const fill = fillOf(el);
            const bodyPad = tokens(styleOf(el), where).padPx;
            if (unit && (fill || bodyPad !== undefined)) kit.look(unit, { ...(fill ? { fill } : {}), ...(bodyPad !== undefined ? { padPx: bodyPad } : {}) });
            compileChildren(el.kids, parent, el);
            return;
        }
        const asDot = isDot(el);
        if (asDot) {
            id = container(el, parent);
        } else if (hasClass(el, 'chip')) {
            const drawn = svgKids(el).filter(k => planSvg(k));
            const label = textOf(el.kids) || (drawn.length ? 'icon' : '');
            const surface = surfaceOf(el);
            if (!label) warn(`${where}.chip has no text; skipped.`);
            else {
                const first = el.kids.findIndex(k => typeof k === 'string' ? k.trim() !== '' : k.tag !== 'svg' && textOf([k]) !== '');
                const leading = drawn.filter(k => first < 0 || el.kids.indexOf(k) < first);
                id = kit.chip(label, {
                    into: parent, name: nameFor(el, `chip ${label.slice(0, 24)}`), ...(surface ? { surface } : {}),
                    ...(drawn.length ? { leading: (at: string) => { for (const k of leading) emitSvg(k, at); }, iconOnly: textOf(el.kids) === '' } : {}),
                });
                for (const k of drawn) if (!leading.includes(k)) emitSvg(k, id);
            }
        } else if (CONTAINERS.has(el.tag) || hasClass(el, 'split')) {
            id = container(el, parent);
        } else if (/^h[1-6]$/.test(el.tag) || ['p', 'small', 'label', 'blockquote', 'strong', 'b', 'em', 'i', 'dt', 'dd'].includes(el.tag)) {
            if (allInline(el.kids)) {
                const content = textOf(el.kids);
                if (content) {
                    const bold = wholeStrong(el) || el.tag === 'strong' || el.tag === 'b';
                    const groups = styleRuns(el);
                    if (groups.length > 1 && groups.length <= 6) {
                        // Inline children set in their own colour, face or size ("Better coffee. <em>Brighter mornings.</em>"): a line of texts, one per style.
                        const center = computedOf(el).align === 'center';
                        id = kit.stack('x', { into: parent, name: nameFor(el, `${el.tag} ${content.slice(0, 24)}`), gap: 's1', inline: true });
                        kit.behavior(id, 'Layout', { alignMain: center ? 'Center' : computedOf(el).align === 'right' ? 'End' : 'Start', alignCross: 'End' });
                        for (const g of groups) emitText(g.el, ROLE[el.tag], g.text, { into: id, ...(bold ? { bold: true } : {}) });
                    } else {
                        id = emitText(el, ROLE[el.tag], content, { into: parent, name: nameFor(el, `${el.tag} ${content.slice(0, 24)}`), ...(bold ? { bold: true } : {}) });
                    }
                }
            } else {
                // Block content inside a text tag: keep the structure as a stack.
                id = kit.stack('y', { into: parent, name: nameFor(el, el.tag) });
                compileChildren(el.kids, id, el);
            }
        } else if (el.tag === 'ul' || el.tag === 'ol') {
            id = kit.stack('y', { into: parent, name: nameFor(el, el.tag === 'ul' ? 'List' : 'Numbered list'), gap: 's1' });
            let index = 0;
            for (const k of el.kids) {
                if (typeof k === 'string') { if (k.trim()) warn(`${where}: text outside <li> was ignored.`); continue; }
                if (k.tag !== 'li') { warn(`${where}: <${k.tag}> inside a list is not a list item; ignored.`); continue; }
                index++;
                const mark = el.tag === 'ul' ? '• ' : `${index}. `;
                const boxed = /^(inline-)?(flex|grid)$/.test(styleOf(k).display ?? '');
                if (allInline(k.kids) && !boxed) {
                    const content = textOf(k.kids);
                    if (content) {
                        const li = emitText(k, 'body', mark + content, { into: id, name: nameFor(k, `item ${content.slice(0, 24)}`) });
                        enter(k, li);
                        motion(k, li);
                    }
                } else {
                    const li = boxed ? container(k, id) : kit.stack('y', { into: id, name: nameFor(k, 'Item') });
                    if (!boxed) compileChildren(k.kids, li, k);
                    enter(k, li);
                    motion(k, li);
                }
            }
        } else if (el.tag === 'li') {
            warn('<li> outside <ul> or <ol>; built as text.');
            id = kit.text('body', '• ' + textOf(el.kids), { into: parent });
        } else if (el.tag === 'img') {
            const alt = el.attrs.alt?.trim();
            if (!alt) warn('<img> has no alt text, which is the image prompt; used "Image".');
            const aspect = (el.attrs['data-aspect'] ?? '4:3').replace('/', ':');
            id = kit.image(alt || 'Image', aspect, { into: parent, name: nameFor(el, `Image ${(alt ?? '').slice(0, 24)}`), ...lead() });
        } else if (el.tag === 'figure') {
            const img = el.kids.find((k): k is El => typeof k !== 'string' && k.tag === 'img');
            const cap = el.kids.find((k): k is El => typeof k !== 'string' && k.tag === 'figcaption');
            const asLead = lead().lead === true;
            for (const k of el.kids) if (typeof k !== 'string' && k !== img && k !== cap) warn(`${where}: <${k.tag}> inside a figure is ignored.`);
            if (!img) warn('<figure> has no <img>; skipped.');
            else {
                const alt = img.attrs.alt?.trim();
                if (!alt) warn('<img> has no alt text, which is the image prompt; used "Image".');
                const aspect = (img.attrs['data-aspect'] ?? '4:3').replace('/', ':');
                if (cap) id = kit.figure(alt || 'Image', aspect, textOf(cap.kids) || ' ', { into: parent, name: nameFor(el, 'Figure'), ...(asLead ? { lead: true } : {}) });
                else id = kit.image(alt || 'Image', aspect, { into: parent, name: nameFor(el, 'Figure'), ...(asLead ? { lead: true } : {}) });
            }
        } else if (el.tag === 'dl') {
            const items: { label: string; value: string }[] = [];
            let label: string | undefined;
            for (const k of el.kids) {
                if (typeof k === 'string') continue;
                if (k.tag === 'dt') label = textOf(k.kids);
                else if (k.tag === 'dd') { items.push({ label: label ?? '', value: textOf(k.kids) }); label = undefined; }
                else warn(`${where}: <${k.tag}> inside a dl is ignored.`);
            }
            if (!items.length) warn('<dl> has no dt/dd pairs; skipped.');
            else id = kit.facts(items, { into: parent, name: nameFor(el, 'Facts') });
        } else if (el.tag === 'button') {
            // A button of only a drawable icon needs no label: the icon is what it shows, and the name is only the object's.
            const label = textOf(el.kids) || (el.attrs['aria-label'] ?? el.attrs.title ?? '').trim() || (svgKids(el).some(k => planSvg(k)) ? 'icon' : '');
            if (!label) warn('<button> has no text, aria-label or title to use as its label; skipped.');
            else {
                const fill = fillOf(el);
                const box = boxOf(el);
                const css = styleOf(el);
                // The page's own button: its fill (or none), border, corners and padding. A class on a Style surface keeps that surface.
                const named = surfaceOf(el);
                const surface = named ?? (fill ? nearestSurface(fill.hex) : box.edge ? nearestSurface(behindOf(el)?.hex ?? '#EFEDE8') : undefined);
                const bare = named === undefined && !fill && !box.edge;
                let into = parent;
                let rim: string | undefined;
                if (box.edge && !named) {
                    rim = kit.panel(nearestSurface(box.edge.color.hex), { into: parent, name: nameFor(el, `button ${label.slice(0, 24)}`) + ' Rim', gap: 's0', padding: 's0', dir: 'y' });
                    kit.look(rim, { fill: box.edge.color, padding: box.edge.width / CANVAS_PX, ...(box.radius !== undefined ? { radius: box.radius / CANVAS_PX, ...(box.pill ? { pill: true } : {}) } : {}) });
                    into = rim;
                }
                // Icons in the button sit beside its label, in the order the markup has them; a button of only an icon has no label to draw.
                const icons = svgKids(el);
                const firstText = el.kids.findIndex(k => typeof k === 'string' ? k.trim() !== '' : k.tag !== 'svg' && textOf([k]) !== '');
                const leading = icons.filter(k => firstText < 0 || el.kids.indexOf(k) < firstText);
                id = kit.button(label, {
                    into, name: nameFor(el, `button ${label.slice(0, 24)}`), ...(surface && !bare ? { surface } : {}), ...(bare ? { bare: true } : {}),
                    ...(icons.length ? { leading: (at: string) => { for (const k of leading) emitSvg(k, at); }, iconOnly: textOf(el.kids) === '' && icons.some(k => planSvg(k)) } : {}),
                });
                for (const k of icons) if (!leading.includes(k)) emitSvg(k, id);
                const nested = (n: El): El[] => n.kids.flatMap(k => typeof k === 'string' ? [] : k.tag === 'svg' ? (icons.includes(k) ? [] : [k]) : nested(k));
                if (nested(el).length) warn(`<button> "${label.slice(0, 24)}": an <svg> inside its text (not a direct child) is not drawn.`);
                const tl = textLook(el);
                const pad = ['padding'].map(k => css[k]).filter(Boolean)[0];
                const padPx = pad === undefined ? null : Math.max(...pad.split(/\s+/).map(x => lengthPx(x) ?? 0));
                const radius = box.radius === undefined ? undefined : Math.max(0, box.radius - (box.edge?.width ?? 0));
                const behind = box.edge && !fill && !named ? behindOf(el) : undefined;
                const wide = hugsInColumn(el);
                if (wide) kit.hug(rim ?? id, wide);
                kit.look(id, {
                    ...tl.look, ...(fill ? { fill } : behind ? { fill: behind } : {}),
                    ...(radius !== undefined ? { radius: radius / CANVAS_PX, ...(box.pill ? { pill: true } : {}) } : {}),
                    ...(padPx !== null && padPx > 0 ? { padding: padPx / CANVAS_PX } : {}),
                    ...(box.shadows.length ? { shadows: box.shadows } : {}), ...(box.glow ? { glow: box.glow } : {}), ...(box.translucent ? { translucent: true } : {}),
                });
                // The button is wired; a bordered one stands in the flow as its rim, which takes the entrance, motion, id and flex share below.
                wire(el, id);
                if (rim) id = rim;
            }
        } else if (el.tag === 'svg') {
            id = emitSvg(el, parent);
        } else if (el.tag === 'stage3d') {
            const name = el.attrs.name || el.attrs['data-name'] || el.attrs.id;
            const aspect = el.attrs.aspect ?? el.attrs['data-aspect'];
            id = kit.stage3d(name, aspect?.replace('/', ':'), { into: parent, ...lead() });
            // A stage that fills its parent (height:100%) takes the parent's px height where the page fixes it; the layout would size it by its aspect.
            const own = placedSize.get(el);
            const up = parentOf.get(el);
            const reads = [...(own?.reads ?? [])];
            let fillH = own?.h;
            if (fillH === undefined && declared(el, 'height') === '100%' && up) fillH = innerHeight(up);
            if (fillH !== undefined && declared(el, 'height') === '100%') reads.push([el, 'height']);
            if (own?.w !== undefined || fillH !== undefined) {
                kit.fixed(id, { ...(own?.w !== undefined ? { w: own.w } : {}), ...(fillH !== undefined ? { h: fillH } : {}) });
                for (const [owner, prop] of reads) readSize(owner, prop);
            }
            if (name) result.ids[name] = id;
        } else if (el.tag === 'br') {
            return;
        } else if (el.tag === 'hr') {
            warn('<hr> is not supported; skipped.');
            return;
        } else if (el.tag === 'style') {
            return;
        } else if (RAW_TEXT.has(el.tag)) {
            warn(`<${el.tag}> is not supported (no scripts); skipped.`);
            return;
        } else if (INLINE.has(el.tag) && svgKids(el).length) {
            // An inline element that holds an icon (a brand mark and its name): a row of its parts.
            id = kit.stack('x', { into: parent, name: nameFor(el, el.tag), gap: 's1', inline: true });
            kit.behavior(id, 'Layout', { alignMain: 'Start', alignCross: 'Center' });
            compileChildren(el.kids, id, el, true);
        } else if (INLINE.has(el.tag)) {
            // A block-level use of an inline tag: its content stands as text.
            const content = textOf(el.kids);
            if (content) id = emitText(el, 'body', content, { into: parent });
        } else {
            warn(`<${el.tag}> is not supported; its content was placed in the parent.`);
            compileChildren(el.kids, parent, el);
            return;
        }
        if (id !== undefined) {
            enter(el, id);
            motion(el, id);
            if (el.attrs.id) result.ids[el.attrs.id] = id;
            const grow = parseFloat(styleOf(el)['flex-grow'] ?? styleOf(el).flex?.split(/\s+/)[0] ?? '');
            if (Number.isFinite(grow) && grow > 0) kit.weigh(id, grow);
        }
        return id;
    }

    {
        const any = (el: El): boolean => el.kids.some(k => typeof k !== 'string' && (styleOf(k)['font-size'] !== undefined || any(k)));
        faithful = any(tree);
        if (faithful) kit.faithful();
    }
    compileChildren(tree.kids, root, tree);
    // A size nothing read is reported, as every ignored size was before some were read.
    for (const el of sizedEls) {
        for (const [key, value] of Object.entries(styleOf(el))) {
            const prop = key.slice(IGNORED_SIZE.length);
            if (key.startsWith(IGNORED_SIZE) && !sizeRead.get(el)?.has(prop)) (el.tag === 'svg' ? svgWarn : warn)(ignoredSizeMessage(`<${el.tag}>`, prop, value));
        }
    }

    if (entering.length) {
        if (!unit) warn('data-enter needs the markup to sit in a unit; entrances were not started.');
        else {
            const have = propsOf(unit);
            setProps(unit, {
                properties: have.properties ?? [],
                states: { ...(have.states ?? {}), away: { values: {} }, home: { values: {} } },
            });
            kit.interaction(unit)?.onStart().setGroupState(unit, 'home');
        }
    }
    return result;
}
